import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import DateSelect from '../shared/DateSelect';
import LocationSelect from '../shared/LocationSelect';
import { useNavigate, useParams } from 'react-router-dom';
import { getTherapistAvailability } from '../../../../helper/api.helper';
import toast from 'react-hot-toast';
import SessionSelect from '../shared/SessionSelect';
import TimeslotSelect from '../shared/TimeslotSelect';

const getMaximumBookingDays = (locations, selectedLocation) => {
  const maximumBookingDays = Number(
    locations.find((location) => location.id === selectedLocation)?.maximumBookingDays
  );
  return Number.isFinite(maximumBookingDays) && maximumBookingDays > 0 ? maximumBookingDays : 30;
};

const isAfterMaximumBookingWindow = (date, maximumBookingDays) => {
  const selectedDay = new Date(date);
  selectedDay.setHours(0, 0, 0, 0);

  const lastBookableDay = new Date();
  lastBookableDay.setHours(0, 0, 0, 0);
  lastBookableDay.setDate(lastBookableDay.getDate() + maximumBookingDays);

  return selectedDay > lastBookableDay;
};

const getMaximumBookingWindowMessage = (maximumBookingDays) =>
  `You can book this session only up to ${maximumBookingDays} days from today. Please choose an earlier date.`;

const ConsultationDetails = ({
  selectedEventType,
  handleEventTypeChange,
  selectedLocation,
  handleLocationChange,
  therapist,
  selectedDate,
  handleDateClick,
  selectedTimeIndex,
  handleTimeClick,
  services,
  locations,
  setLoading,
  selectedTime
}) => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [timeSlots, setTimeSlots] = useState([]);
  const maximumBookingDays = getMaximumBookingDays(locations, selectedLocation);
  const unavailableMessage = isAfterMaximumBookingWindow(selectedDate, maximumBookingDays)
    ? getMaximumBookingWindowMessage(maximumBookingDays)
    : '';

  useEffect(() => {
    if (id && selectedDate && selectedEventType && selectedLocation) {
      if (isAfterMaximumBookingWindow(selectedDate, maximumBookingDays)) {
        setTimeSlots([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      getTherapistAvailability(id, {
        serviceId: selectedEventType,
        locationId: selectedLocation,
        selectedDate: selectedDate.getTime(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.status === 'success') {
            setTimeSlots(res.data.slots);
            setLoading(false);
          } else {
            setLoading(false);

            toast.error(res.message);
          }
        })
        .catch((err) => {
          setLoading(false);

          toast.error(err.message);
        });
    }
  }, [id, selectedDate, selectedEventType, selectedLocation, maximumBookingDays]);

  const navigateToCheckout = () => {
    const timestamp = new Date(selectedDate).getTime();
    navigate(
      `/therapists/${id}/checkout?date=${timestamp}&eventType=${selectedEventType}&location=${selectedLocation}&time=${encodeURIComponent(JSON.stringify(selectedTime))}`
    );
  };

  return (
    <>
      <div>
        <SessionSelect
          selectedEventType={selectedEventType}
          handleEventTypeChange={handleEventTypeChange}
          services={services}
        />

        <LocationSelect
          selectedLocation={selectedLocation}
          selectedEventType={selectedEventType}
          handleLocationChange={handleLocationChange}
          therapist={therapist}
          locations={locations}
          services={services}
        />

        <DateSelect
          selectedDate={selectedDate}
          handleDateClick={handleDateClick}
          maximumBookingDays={maximumBookingDays}
        />
      </div>
      <div>
        <TimeslotSelect
          timeSlots={timeSlots}
          selectedTimeIndex={selectedTimeIndex}
          handleTimeClick={handleTimeClick}
          unavailableMessage={unavailableMessage}
        />
      </div>
      <div className="margin-top--md" style={{ textAlign: 'start' }}>
        <Button
          disabled={
            !selectedDate ||
            !selectedEventType ||
            !selectedLocation ||
            !!unavailableMessage ||
            !selectedTime
          }
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          onClick={navigateToCheckout}
          sx={{ textTransform: 'none', maxWidth: '350px' }}>
          Proceed to checkout
        </Button>
      </div>
      <style jsx>{`
        p,
        h3,
        h4,
        .main-div > span,
        div {
          color: black;
        }
      `}</style>
    </>
  );
};

export default ConsultationDetails;
